import {
  AfterViewInit,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BaseDirective } from '@egym/ngx-adm-int';
import { GymAddress } from 'src/app/model/gym-address';
import { GymFinderService } from 'src/app/service/gymfinder.service';
import {
  Observable,
  Subject,
  Subscription,
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  of,
  switchMap,
} from 'rxjs';
import { FormControl, NgForm } from '@angular/forms';
import { GeocodingPrediction } from 'src/app/model/geocoding-prediction';

@Component({
  selector: 'gymfinder-validate-address-dialog',
  templateUrl: './validate-address-dialog.component.html',
  styleUrls: ['./validate-address-dialog.component.scss'],
  standalone: false,
})
export class ValidateAddressDialogComponent
  extends BaseDirective<GymAddress>
  implements OnInit, AfterViewInit, OnDestroy
{
  constructor(
    snackBar: MatSnackBar,
    private gymFinderService: GymFinderService,
    private dialogRef: MatDialogRef<ValidateAddressDialogComponent>,
    @Inject(MAT_DIALOG_DATA) private data: GymAddress
  ) {
    super(snackBar);
  }

  address: GymAddress;

  formChangeSubscription?: Subscription;

  selectedPredictionSubscription?: Subscription;

  isResolved = false;

  isLoading = false;

  @ViewChild('addressForm', { static: true })
  public addressForm: NgForm;

  addressSearchControl = new FormControl<string>('');

  predictions$: Observable<GeocodingPrediction[]>;
  selectedPrediction$ = new Subject<GeocodingPrediction>();

  ngOnInit(): void {
    this.address = this.data;

    this.predictions$ = this.addressSearchControl.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      filter((value): value is string => typeof value === 'string'),
      switchMap((value) =>
        this.gymFinderService
          .autocompleteAddress(value)
          .pipe(catchError(() => of([])))
      )
    );

    this.selectedPredictionSubscription = this.selectedPrediction$
      .pipe(
        switchMap((prediction) => {
          this.isLoading = true;

          return this.gymFinderService.findPlaceById(prediction.placeId).pipe(
            catchError(() => {
              this.openSnackBar('Could not load this address, please try again');
              this.isResolved = false;
              return of(null);
            }),
            finalize(() => {
              this.isLoading = false;
            })
          );
        })
      )
      .subscribe((geocodingAddress) => {
        if (!geocodingAddress) {
          return;
        }

        const {
          longitude,
          latitude,
          administrativeArea1: stateOrProvince,
          street,
          streetNumber,
          city,
          zipCode,
          country,
        } = geocodingAddress;

        this.address = {
          street,
          streetNumber,
          city,
          zipCode,
          country,
          stateOrProvince,
          coordinates: [longitude, latitude],
        };

        this.isResolved = true;

        this.addressForm.resetForm({
          street,
          streetNumber,
          city,
          zipCode,
          country,
          stateOrProvince,
          longitude,
          latitude,
        });
      });
  }

  ngOnDestroy() {
    if (this.formChangeSubscription) {
      this.formChangeSubscription.unsubscribe();
    }

    if (this.selectedPredictionSubscription) {
      this.selectedPredictionSubscription.unsubscribe();
    }
  }

  ngAfterViewInit() {
    this.formChangeSubscription = this.addressForm.valueChanges?.subscribe({
      next: () => {
        if (!this.addressForm.pristine) {
          this.isResolved = false;
        }
      },
    });
  }

  closeDialog() {
    this.dialogRef.close();
  }

  confirmAddress() {
    this.dialogRef.close(this.address);
  }

  onAddressSelected(prediction: GeocodingPrediction): void {
    this.selectedPrediction$.next(prediction);
  }

  displayPrediction(prediction: GeocodingPrediction): string {
    return prediction ? prediction.description : '';
  }
}
